import * as React from "react"
import * as DialogPrimitive from "@radix-ui/react-dialog"
import { X } from "@untitledui/icons"
import { cx } from "@/lib/utils/cx"

// Radix dialog wrapped in our design vocabulary — soft backdrop, rounded-3xl
// content, our zinc/emerald color story. Mirrors DesignSelect / DesignMenu.
// New code should import DesignDialog instead of the shadcn-style
// dialog.tsx (which uses bg-background theme tokens that don't match).
//
// Usage:
//
//   <DesignDialog.Root open={open} onOpenChange={setOpen}>
//     <DesignDialog.Content>
//       <DesignDialog.Header>
//         <DesignDialog.Title>...</DesignDialog.Title>
//         <DesignDialog.Description>...</DesignDialog.Description>
//       </DesignDialog.Header>
//       <DesignDialog.Body>...form...</DesignDialog.Body>
//       <DesignDialog.Footer>
//         <button>Cancel</button>
//         <button>Save</button>
//       </DesignDialog.Footer>
//     </DesignDialog.Content>
//   </DesignDialog.Root>

const Root = DialogPrimitive.Root
const Trigger = DialogPrimitive.Trigger
const Close = DialogPrimitive.Close

const Overlay = React.forwardRef<
  React.ComponentRef<typeof DialogPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Overlay
    ref={ref}
    className={cx(
      "fixed inset-0 z-50 bg-zinc-900/30 backdrop-blur-sm",
      "data-[state=open]:animate-in data-[state=open]:fade-in-0",
      "data-[state=closed]:animate-out data-[state=closed]:fade-out-0",
      className,
    )}
    {...props}
  />
))
Overlay.displayName = "DesignDialog.Overlay"

interface ContentProps
  extends React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content> {
  width?: "sm" | "md" | "lg"
}

const Content = React.forwardRef<
  React.ComponentRef<typeof DialogPrimitive.Content>,
  ContentProps
>(({ className, children, width = "md", ...props }, ref) => (
  <DialogPrimitive.Portal>
    <Overlay />
    <DialogPrimitive.Content
      ref={ref}
      className={cx(
        "fixed left-1/2 top-1/2 z-50 -translate-x-1/2 -translate-y-1/2 w-[calc(100%-2rem)] rounded-3xl bg-white shadow-2xl shadow-zinc-900/20 border border-zinc-200/70 overflow-hidden",
        "data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=open]:zoom-in-95",
        "data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95",
        width === "sm" && "max-w-md",
        width === "md" && "max-w-lg",
        width === "lg" && "max-w-2xl",
        className,
      )}
      {...props}
    >
      {children}
      <DialogPrimitive.Close
        aria-label="Close"
        className="absolute right-4 top-4 size-8 rounded-lg grid place-items-center text-zinc-400 hover:text-zinc-700 hover:bg-zinc-100 transition-colors duration-150"
      >
        <X className="size-4" aria-hidden />
      </DialogPrimitive.Close>
    </DialogPrimitive.Content>
  </DialogPrimitive.Portal>
))
Content.displayName = "DesignDialog.Content"

function Header({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      className={cx("flex flex-col gap-1 px-6 pt-6 pb-2 pr-12", className)}
      {...props}
    />
  )
}

const Title = React.forwardRef<
  React.ComponentRef<typeof DialogPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Title>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Title
    ref={ref}
    className={cx("text-[18px] font-bold text-zinc-900 tracking-tight", className)}
    {...props}
  />
))
Title.displayName = "DesignDialog.Title"

const Description = React.forwardRef<
  React.ComponentRef<typeof DialogPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Description>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Description
    ref={ref}
    className={cx("text-[13.5px] text-zinc-500 leading-relaxed", className)}
    {...props}
  />
))
Description.displayName = "DesignDialog.Description"

function Body({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) {
  return <div className={cx("px-6 py-4", className)} {...props} />
}

function Footer({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      className={cx(
        "flex items-center justify-end gap-2 px-6 py-4 border-t border-zinc-100 bg-zinc-50/40",
        className,
      )}
      {...props}
    />
  )
}

export const DesignDialog = {
  Root,
  Trigger,
  Close,
  Content,
  Header,
  Title,
  Description,
  Body,
  Footer,
}
